import React, { useCallback } from "react";

import { useBusyBlockerContext } from "../contexts/busy-blocker-context";
import { useDashboardContext } from "../contexts/dashboard-context";
import { AlternativeProductGroup, useAlternativeProducts } from "./useAlternativeProducts";
import { useCart } from "./useCart";
import { useDefinition } from "./useDefinition";
import { useEditor } from "./useEditor";
import { useLabels } from "./useLabels";
import { useProduct } from "./useProduct";
import { useClient } from "./useClient";
import { useResetDefinitionForOptions } from "./useResetDefinitionForOptions";
import { showDialogAsync } from "../components/dialog-queue";
import { ConfirmDialog } from "../components/dialog/confirm";
import { showInfoDialogAsync } from "../components/dialogs/info-dialog";
import { labels } from "../components/bottom-menu-panels/alternative-products/labels";

export function useSetAlternativeProduct() {
	const editor = useEditor();
	const client = useClient();
	const dashboard = useDashboardContext();
	const definition = useDefinition();

	const [definitionToReuse, setDefinitionToReuse] = useResetDefinitionForOptions();
	const { busyBlocker } = useBusyBlockerContext();
	const { isPlaceholderData } = useAlternativeProducts();

	const { designNotAvailableDescription, designNotAvailableTitle, proceed, back } = labels;

	const product = useProduct();

	const [[attributeName, productUnavailable]] = useLabels([
		labels.attributeName(product?.attributeTypeCode),
		labels.productAttributeNotAvailable,
	]);

	const shoppingCart = useCart();

	const applyNewAlternativeProductAsync = useCallback(
		async (code: eXpress.AlternativeProductCode) => {
			if (!definition) return undefined;

			if (!definitionToReuse) {
				const copyApiData = editor.client.copyApiData?.bind(editor.client);
				setDefinitionToReuse(definition.clone(copyApiData));
			}

			return busyBlocker.during(
				dashboard.applyAlternativeProductAsync(code, definitionToReuse),
			);
		},
		[definition, definitionToReuse, busyBlocker, dashboard, setDefinitionToReuse, editor],
	);

	const setAlternativeProductAsync = useCallback(
		async (
			code: eXpress.AlternativeProductCode,
			alternativeProductsGroup: AlternativeProductGroup | undefined,
		) => {
			const selectedAlternativeProduct = alternativeProductsGroup?.alternativeProducts.find(
				(prod) => prod.alternativeProductCode === code,
			);

			// don't do anything if we are showing outdated data from previous fetch
			if (!isPlaceholderData) {
				// check if selected alternative product has required attributes
				if (selectedAlternativeProduct) {
					const selectedQuantities = shoppingCart?.quantities.reduce<
						eXpress.ProductAttributeCode[]
					>((p, c) => {
						if (c.attributeCode !== undefined) {
							p.push(c.attributeCode);
						}
						return p;
					}, []);

					if (selectedQuantities?.length) {
						const alternativeOptions = await client.getProductAttributesAsync(
							selectedAlternativeProduct.productCode,
						);
						if (!alternativeOptions) return undefined;

						const availableAttributeCodes = alternativeOptions.attributes?.values.map(
							(attribute) => attribute.code,
						);
						const unavailableAlternativeOptions = selectedQuantities.reduce<
							eXpress.ProductAttributeCode[]
						>((p, c) => {
							if (!availableAttributeCodes?.includes(c)) {
								p.push(c);
							}
							return p;
						}, []);

						if (
							unavailableAlternativeOptions &&
							unavailableAlternativeOptions.length > 0
						) {
							const unavailableAttributes = unavailableAlternativeOptions.map(
								(attr) => attr.toUpperCase(),
							);
							const message =
								productUnavailable && attributeName
									? eXpress.utils.formatString(
											productUnavailable,
											selectedAlternativeProduct.name,
											attributeName,
											unavailableAttributes.toString(),
									  )
									: "";

							return showInfoDialogAsync({
								messageHtml: message,
							});
						}
					}

					if (selectedAlternativeProduct.willResetDesign) {
						return showDialogAsync(({ resolve, reject }, { isOpen }) => {
							return (
								<ConfirmDialog
									cancelLabel={back}
									titleLabel={designNotAvailableTitle}
									isOpen={isOpen}
									okLabel={proceed}
									messageHtmlLabel={designNotAvailableDescription}
									onOk={() => {
										try {
											applyNewAlternativeProductAsync(code)
												.then(() => resolve())
												.catch((err: unknown) => reject(err));
										} catch (err) {
											reject(err);
										}
									}}
									onCancel={() => resolve(false)}
								/>
							);
						});
					}

					return applyNewAlternativeProductAsync(code);
				}
			}
			return undefined;
		},
		[
			isPlaceholderData,
			shoppingCart?.quantities,
			client,
			attributeName,
			back,
			proceed,
			productUnavailable,
			designNotAvailableTitle,
			designNotAvailableDescription,
			applyNewAlternativeProductAsync,
		],
	);

	return { setAlternativeProductAsync };
}
